import { FC, ReactNode, useMemo, createContext, useContext, useState, useEffect } from 'react'
import { ConnectionProvider, WalletProvider, useWallet } from '@solana/wallet-adapter-react'
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base'
import { log } from '@/utils/logger'
// Phantom and Solflare removed - only Backpack supported for X1
// import {
//   PhantomWalletAdapter,
//   SolflareWalletAdapter,
// } from '@solana/wallet-adapter-wallets'
import {
  SolanaMobileWalletAdapter,
  createDefaultAddressSelector,
  createDefaultAuthorizationResultCache,
  createDefaultWalletNotFoundHandler,
} from '@solana-mobile/wallet-adapter-mobile'
import { clusterApiUrl } from '@solana/web3.js'
import { configService } from '@/config'
import { CustomWalletModal } from '@/components/CustomWalletModal'
// Custom adapter for wallets not available in official package
import { BackpackWalletAdapter } from '@/adapters/BackpackWalletAdapter'
import { X1WalletAdapter } from '@/adapters/X1WalletAdapter'

// Custom wallet modal context
interface WalletModalContextType {
  visible: boolean
  setVisible: (visible: boolean) => void
}

const WalletModalContext = createContext<WalletModalContextType>({
  visible: false,
  setVisible: () => {},
})

export const useWalletModal = () => useContext(WalletModalContext)

interface CustomWalletModalProviderProps {
  children: ReactNode
}

const CustomWalletModalProvider: FC<CustomWalletModalProviderProps> = ({ children }) => {
  const [visible, setVisible] = useState(false)
  const { connected, connecting, wallet } = useWallet()

  // Auto-close modal when wallet connects
  useEffect(() => {
    if (connected || connecting) {
      setVisible(false)
    }
  }, [connected, connecting])

  // Enhanced mobile wallet connection detection and persistence
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When user returns to the app from wallet app, check if wallet is now connected
      if (!document.hidden && !connected && !connecting) {
        // Wait a bit for wallet state to update
        setTimeout(() => {
          // Try to reconnect if wallet was previously selected
          const savedWallet = localStorage.getItem('wallet-adapter-x1-custom')
          if (savedWallet && wallet) {
            log.wallet('Attempting to reconnect wallet after app focus:', savedWallet)
            // The wallet adapter will handle reconnection automatically via autoConnect
          }
        }, 1000)
      }
    }

    // Listen for app focus changes (when user returns from wallet app)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleVisibilityChange)
    }
  }, [connected, connecting, wallet])

  return (
    <WalletModalContext.Provider value={{ visible, setVisible }}>
      {children}
      <CustomWalletModal 
        visible={visible} 
        onClose={() => setVisible(false)} 
      />
    </WalletModalContext.Provider>
  )
}

export interface WalletContextProviderProps {
  children: ReactNode
}

export const CustomWalletContextProvider: FC<WalletContextProviderProps> = ({ children }) => {
  // Get current network configuration
  const currentNetwork = configService.getCurrentNetworkConfig()

  // Configure the RPC endpoint for X1 network (Solana fork)
  // For wallet operations, use the public RPC endpoints which have proper CORS support
  // and don't require API key authentication (unlike Nexus which is for explorer backend)
  const endpoint = useMemo(() => {
    // Use public RPC endpoints for wallet operations
    if (currentNetwork.name === 'mainnet') {
      return 'https://rpc.mainnet.x1.xyz'
    }
    return 'https://rpc.testnet.x1.xyz'
  }, [currentNetwork.name])

  // Configure supported wallets for X1 (Solana-compatible)
  const wallets = useMemo(
    () => {
      try {
        // Only include wallets we explicitly support for X1
        const supportedWallets = []

        // Native X1 Wallet extension (v0.10.98+ injects window.x1Wallet/x1/solana
        // and registers via wallet-standard — the adapter resolves all paths).
        try {
          supportedWallets.push(new X1WalletAdapter())
        } catch (error) {
          log.warn('X1WalletAdapter failed to initialize:', error)
        }

        // Backpack — also supported for X1
        try {
          supportedWallets.push(new BackpackWalletAdapter())
        } catch (error) {
          log.warn('BackpackWalletAdapter failed to initialize:', error)
        }

        // Mobile Wallet Adapter - handles ALL mobile wallet connections
        try {
          supportedWallets.push(
            new SolanaMobileWalletAdapter({
              addressSelector: createDefaultAddressSelector(),
              appIdentity: {
                name: 'X1 Explorer - FortiBlox',
                uri: window.location.origin,
                icon: '/fortiblox-192x192.png',
              },
              authorizationResultCache: createDefaultAuthorizationResultCache(),
              // Use mainnet-beta cluster - X1 is a Solana fork
              cluster: WalletAdapterNetwork.Mainnet,
              onWalletNotFound: createDefaultWalletNotFoundHandler(),
            })
          )
        } catch (error) {
          log.warn('SolanaMobileWalletAdapter failed to initialize:', error)
        }

        // Note: Hardware wallets can be accessed through Phantom/Solflare which support them
        
        // Create a map to track unique wallets by name, excluding MetaMask
        const walletMap = new Map()
        
        supportedWallets.forEach(wallet => {
          // Skip MetaMask to prevent conflicts with other wallets
          if (wallet.name === 'MetaMask') {
            log.warn('Excluding MetaMask to prevent conflicts with Solana wallets')
            return
          }
          
          if (!walletMap.has(wallet.name)) {
            walletMap.set(wallet.name, wallet)
          } else {
            log.warn(`Skipping duplicate wallet: ${wallet.name}`)
          }
        })
        
        const uniqueWallets = Array.from(walletMap.values())
        log.wallet('Custom X1 supported wallets:', uniqueWallets.map(w => `${w.name} (${w.readyState})`))
        
        return uniqueWallets
      } catch (error) {
        log.error('Error configuring X1 wallets:', error)
        return []
      }
    },
    []
  )

  return (
    <ConnectionProvider 
      endpoint={endpoint}
      config={{
        commitment: 'confirmed',
        wsEndpoint: currentNetwork.wsUrl,
        // Enhanced app metadata for wallet branding
        httpHeaders: {
          'X-App-Name': 'X1 Explorer - Fortiblox',
          'X-App-Icon': `${window.location.origin}/fortiblox-192x192.png`,
          'X-App-URL': window.location.origin,
        }
      }}
    >
      <WalletProvider 
        wallets={wallets} 
        autoConnect={true}
        localStorageKey="wallet-adapter-x1-custom"
        onError={(error) => {
          // Only log meaningful errors, suppress recoverable ones
          if (error.message?.includes('User rejected')) {
            log.wallet('User rejected wallet connection')
          } else if (error?.name === 'WalletNotSelectedError') {
            // Suppress WalletNotSelectedError as it's often recoverable
            return
          } else {
            log.warn('Wallet adapter error (may be recoverable):', error?.message || error)
          }
        }}
      >
        <CustomWalletModalProvider>
          {children}
        </CustomWalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  )
}

export default CustomWalletContextProvider